/**
 * Cache Service
 * Serviço para gerenciar dados em cache no navegador usando localStorage
 */

// Configurações padrão para o cache
const DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutos em milissegundos
const CACHE_PREFIX = 'zapcore:cache:';

// Interface para os itens em cache
interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiration: number;
}

// Tipos específicos de itens em cache
interface ConnectionStatusCache {
  connected: boolean;
  mode?: string;
  error?: string;
  isReady?: boolean;
  timestamp: number;
}

class CacheService {
  /**
   * Armazena um item no cache
   * @param key Chave do item
   * @param data Dados a serem armazenados
   * @param duration Duração do cache em milissegundos (opcional)
   */
  set<T>(key: string, data: T, duration?: number): void {
    try {
      const timestamp = Date.now();
      const expiration = timestamp + (duration || DEFAULT_CACHE_DURATION);
      
      const cacheItem: CacheItem<T> = {
        data,
        timestamp,
        expiration
      };
      
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheItem));
      console.log(`[CacheService] Item "${key}" armazenado em cache até ${new Date(expiration).toLocaleTimeString()}`);
    } catch (error) {
      console.error(`[CacheService] Erro ao armazenar item "${key}" em cache:`, error);
    }
  }
  
  /**
   * Obtém um item do cache
   * @param key Chave do item
   * @param ignoreExpiration Se verdadeiro, retorna o item mesmo se expirado
   * @returns O item armazenado ou null se não encontrado ou expirado
   */
  get<T>(key: string, ignoreExpiration: boolean = false): T | null {
    try {
      const cachedItem = localStorage.getItem(CACHE_PREFIX + key);
      
      if (!cachedItem) {
        return null;
      }
      
      const cacheItem: CacheItem<T> = JSON.parse(cachedItem);
      const now = Date.now();
      
      // Verificar se o item expirou
      if (!ignoreExpiration && cacheItem.expiration < now) {
        console.log(`[CacheService] Item "${key}" expirado (${Math.round((now - cacheItem.expiration) / 1000)}s atrás)`);
        localStorage.removeItem(CACHE_PREFIX + key);
        return null;
      }
      
      console.log(`[CacheService] Item "${key}" recuperado do cache (${Math.round((now - cacheItem.timestamp) / 1000)}s de idade)`);
      return cacheItem.data;
    } catch (error) {
      console.error(`[CacheService] Erro ao obter item "${key}" do cache:`, error);
      return null;
    }
  }
  
  /**
   * Remove um item do cache
   * @param key Chave do item a ser removido
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(CACHE_PREFIX + key);
      console.log(`[CacheService] Item "${key}" removido do cache`);
    } catch (error) {
      console.error(`[CacheService] Erro ao remover item "${key}" do cache:`, error);
    }
  }
  
  /**
   * Limpa todos os itens de cache
   */
  clear(): void {
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(CACHE_PREFIX))
        .forEach(key => localStorage.removeItem(key));
      
      console.log('[CacheService] Cache limpo completamente');
    } catch (error) {
      console.error('[CacheService] Erro ao limpar o cache:', error);
    }
  }
  
  /**
   * Verifica se um item está em cache e não expirou
   * @param key Chave do item
   * @returns Verdadeiro se o item existir e não tiver expirado
   */
  has(key: string): boolean {
    try {
      const cachedItem = localStorage.getItem(CACHE_PREFIX + key);
      
      if (!cachedItem) {
        return false;
      }
      
      const cacheItem = JSON.parse(cachedItem);
      return cacheItem.expiration > Date.now();
    } catch (error) {
      console.error(`[CacheService] Erro ao verificar existência do item "${key}" no cache:`, error);
      return false;
    }
  }
  
  /**
   * Obtém o status de conexão em cache
   * @returns O status de conexão armazenado em cache ou null se não encontrado
   */
  getConnectionStatus(): ConnectionStatusCache | null {
    return this.get<ConnectionStatusCache>('n8n:connectionStatus');
  }
  
  /**
   * Armazena o status de conexão em cache
   * @param status Status da conexão
   * @param duration Duração do cache em milissegundos (opcional)
   */
  setConnectionStatus(status: Omit<ConnectionStatusCache, 'timestamp'>, duration?: number): void {
    const cacheData: ConnectionStatusCache = {
      ...status,
      timestamp: Date.now()
    };
    
    this.set<ConnectionStatusCache>('n8n:connectionStatus', cacheData, duration);
  }
  
  /**
   * Remove o status de conexão do cache
   */
  removeConnectionStatus(): void {
    this.remove('n8n:connectionStatus');
  }
  
  /**
   * Remove todos os itens do cache cujas chaves começam com o prefixo especificado
   * @param prefix Prefixo para filtrar as chaves a serem removidas
   */
  removeByPrefix(prefix: string): void {
    try {
      const fullPrefix = CACHE_PREFIX + prefix;
      let removedCount = 0;
      
      Object.keys(localStorage)
        .filter(key => key.startsWith(fullPrefix))
        .forEach(key => {
          localStorage.removeItem(key);
          removedCount++;
        });
      
      console.log(`[CacheService] Removidos ${removedCount} itens com prefixo "${prefix}" do cache`);
    } catch (error) {
      console.error(`[CacheService] Erro ao remover itens com prefixo "${prefix}" do cache:`, error);
    }
  }
}

// Exportar uma instância singleton
export const cacheService = new CacheService(); 